import React, { useMemo, useState, useEffect, useRef } from "react";
import Button from "../../../components/ui/Button";
import Icon from "../../../components/AppIcon";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { enGB } from "date-fns/locale";

const USE_SEASONAL_IN_TOTAL = true;

// Helpers
const monthLabel = (m) =>
  new Date(2000, m - 1, 1).toLocaleString(undefined, { month: "long" });

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const diffNights = (a, b) =>
  Math.ceil((startOfDay(b) - startOfDay(a)) / (1000 * 60 * 60 * 24));

const getMonthsInRange = (start, end) => {
  if (!start || !end) return [];
  const s = new Date(start.getFullYear(), start.getMonth(), 1);
  const e = new Date(end.getFullYear(), end.getMonth(), 1);
  const months = [];
  let y = s.getFullYear();
  let m = s.getMonth() + 1;
  const endY = e.getFullYear();
  const endM = e.getMonth() + 1;
  while (y < endY || (y === endY && m <= endM)) {
    months.push(m);
    m++;
    if (m === 13) {
      m = 1;
      y++;
    }
  }
  return months;
};
const dayKey = (month, day) => month * 100 + day; // npr. 8*100+22 = 822

const SeasonalPreview = ({
  months,
  getRanges,
  guests = 2,
  checkinDate,
  checkoutDate,
}) => {
  if (!months?.length) return null;
  const extraGuests = Math.max(0, (guests ?? 2) - 2);
  return (
    <div className="p-3 bg-muted/40 rounded-lg border space-y-3">
      {months.map((m) => {
        const ranges = getRanges(m);
        if (!ranges.length) return null;

        const hasBoth = checkinDate && checkoutDate;

        const startKey = hasBoth
          ? dayKey(checkinDate.getMonth() + 1, checkinDate.getDate())
          : null;

        const endKey = hasBoth
          ? dayKey(checkoutDate.getMonth() + 1, checkoutDate.getDate() - 1)
          : null;

        const filtered = hasBoth
          ? ranges.filter((r) => {
              const rStart = dayKey(m, r.start);
              const rEnd = dayKey(m, r.end);
              return rEnd >= startKey && rStart <= endKey;
            })
          : ranges;

        if (!filtered.length) return null;

        return (
          <div key={m}>
            <div className="text-sm font-medium mb-2">
              Seasonal pricing for {monthLabel(m)}
            </div>
            <ul className="text-sm space-y-1">
              {filtered.map((r, i) => (
                <li key={`${m}-${i}`} className="flex justify-between">
                  <span>
                    {String(r.start).padStart(2, "0")}.
                    {String(m).padStart(2, "0")} –{" "}
                    {String(r.end).padStart(2, "0")}.
                    {String(m).padStart(2, "0")}
                  </span>
                  <span className="font-semibold">
                    €{(r.base ?? r.price) + extraGuests * (r.extra ?? 0)}/night
                    {r.extra ? (
                      <span className="text-muted-foreground font-normal">
                        {" "}
                        (+€{r.extra}/extra guest)
                      </span>
                    ) : null}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

const BookingPanel = ({ apartment, onPriceChange, onDatesChange }) => {
  const [checkinDate, setCheckinDate] = useState(null); // Date|null
  const [checkoutDate, setCheckoutDate] = useState(null); // Date|null
  const [guests, setGuests] = useState(2);
  const [showDetails, setShowDetails] = useState(false);

  const seasonalPrices = apartment?.seasonalPrices ?? {};
  const lastSentRef = useRef("__INIT__");

  const getPriceRangesForMonth = (month) => seasonalPrices?.[month] || [];
  const getSeasonalPriceForDate = (date, guestsCount = 2) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const ranges = seasonalPrices?.[month] || [];
    const match = ranges.find((r) => day >= r.start && day <= r.end);
    if (!match) return null;

    const base = match.base ?? match.price ?? 0;
    const extra = match.extra ?? 0;

    const extraGuests = Math.max(0, (guestsCount || 2) - 2);
    return base + extraGuests * extra;
  };

  const monthsToShow =
    checkinDate && checkoutDate
      ? getMonthsInRange(checkinDate, checkoutDate)
      : checkinDate
      ? [checkinDate.getMonth() + 1]
      : checkoutDate
      ? [checkoutDate.getMonth() + 1]
      : [];

  // Emit selected-day nightly price (avoid loops)
  useEffect(() => {
    if (!onPriceChange) return;
    const dateObj = checkinDate || checkoutDate;
    const nextValue = dateObj
      ? getSeasonalPriceForDate(dateObj, guests) ?? apartment?.price ?? 0
      : null;
    if (lastSentRef.current !== nextValue) {
      onPriceChange(nextValue);
      lastSentRef.current = nextValue;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkinDate, checkoutDate, guests]);

  // Totals
  const { nights, subtotal, total, averageNightly } = useMemo(() => {
    if (!checkinDate || !checkoutDate) {
      return {
        nights: 0,
        subtotal: 0,
        total: 0,
        averageNightly: 0,
      };
    }
    const nightsCount = Math.max(0, diffNights(checkinDate, checkoutDate));
    if (nightsCount <= 0) {
      return {
        nights: 0,
        subtotal: 0,
        total: 0,
        averageNightly: 0,
      };
    }
    let computedSubtotal = 0;
    if (USE_SEASONAL_IN_TOTAL) {
      const d = new Date(checkinDate);
      for (let i = 0; i < nightsCount; i++) {
        const nightly =
          getSeasonalPriceForDate(d, guests) ?? apartment?.price ?? 0;
        computedSubtotal += nightly;
        d.setDate(d.getDate() + 1);
      }
    } else {
      computedSubtotal = nightsCount * (apartment?.price ?? 0);
    }
    const grand = computedSubtotal;
    return {
      nights: nightsCount,
      subtotal: computedSubtotal,

      total: grand,
      averageNightly:
        nightsCount > 0 ? Math.round(computedSubtotal / nightsCount) : 0,
    };
  }, [checkinDate, checkoutDate, apartment, guests]);

  const minStay = apartment?.availability?.minStay || 1;
  const today = new Date();

  return (
    <>
      {/* Desktop Sticky Panel */}
      <div className="hidden lg:block sticky top-24">
        <div className="bg-card rounded-xl p-6 border shadow-subtle">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <span className="font-heading text-2xl font-bold text-primary">
                Price helper
              </span>
            </div>
          </div>

          <div className="space-y-4">
            {/* Date pickers */}
            <div className="grid grid-cols-2 gap-2">
              <div className="w-full">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Check-in
                </label>
                <DatePicker
                  selected={checkinDate}
                  onChange={(date) => {
                    setCheckinDate(date);
                    if (checkoutDate && date && checkoutDate <= date) {
                      const next = new Date(date);
                      next.setDate(next.getDate() + Math.max(1, minStay));
                      setCheckoutDate(next);
                      onDatesChange?.({ checkin: date, checkout: next });
                    } else {
                      onDatesChange?.({
                        checkin: date,
                        checkout: checkoutDate,
                      });
                    }
                  }}
                  minDate={today}
                  locale={enGB}
                  dateFormat="dd.MM.yyyy"
                  className="w-full h-10 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none"
                  placeholderText="dd.MM.yyyy"
                />
              </div>

              <div className="w-full">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Check-out
                </label>
                <DatePicker
                  selected={checkoutDate}
                  onChange={(date) => {
                    setCheckoutDate(date);
                    onDatesChange?.({ checkin: checkinDate, checkout: date });
                  }}
                  minDate={checkinDate || today}
                  locale={enGB}
                  dateFormat="dd.MM.yyyy"
                  className="w-full h-10 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none"
                  placeholderText="dd.MM.yyyy"
                />
              </div>
            </div>

            {/* Guests */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Guests
              </label>
              <select
                value={guests}
                onChange={(e) => setGuests(Number(e.target.value))}
                className="w-full h-10 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {[...Array(apartment?.capacity || 4)]?.map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1} {i + 1 === 1 ? "guest" : "guests"}
                  </option>
                ))}
              </select>
            </div>
            <SeasonalPreview
              months={monthsToShow}
              getRanges={getPriceRangesForMonth}
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
            />

            {/* Price breakdown */}
            {nights > 0 && (
              <div className="space-y-2 pt-4 border-t">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">
                    {USE_SEASONAL_IN_TOTAL
                      ? `Average €${averageNightly} / night × ${nights} nights`
                      : `€${apartment?.price} × ${nights} nights`}
                  </span>
                  <span className="text-foreground">€{subtotal}</span>
                </div>
                <div className="flex justify-between font-semibold pt-2 border-t">
                  <span className="text-foreground">Total</span>
                  <span className="text-primary">€{total}</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3 mt-6">
              {nights < minStay && checkinDate && checkoutDate && (
                <div className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-2">
                  Minimum {minStay} {minStay === 1 ? "night" : "nights"}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="lg:hidden">
        <div className="bg-card rounded-xl p-4 border shadow-subtle">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-heading text-xl font-bold text-primary">
                Price helper
              </span>
            </div>

            {nights > 0 && (
              <div className="text-sm text-foreground">
                {nights} night{nights > 1 ? "s" : ""} • Total: €{total}
              </div>
            )}
          </div>

          <div className="mt-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="w-full">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Check-in
                </label>
                <DatePicker
                  selected={checkinDate}
                  onChange={(date) => {
                    setCheckinDate(date);
                    if (checkoutDate && date && checkoutDate <= date) {
                      const next = new Date(date);
                      next.setDate(next.getDate() + Math.max(1, minStay));
                      setCheckoutDate(next);
                      onDatesChange?.({ checkin: date, checkout: next });
                    } else {
                      onDatesChange?.({
                        checkin: date,
                        checkout: checkoutDate,
                      });
                    }
                  }}
                  minDate={today}
                  locale={enGB}
                  dateFormat="dd.MM.yyyy"
                  className="w-full h-10 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none"
                  placeholderText="dd.MM.yyyy"
                />
              </div>
              <div className="w-full">
                <label className="block text-sm font-medium text-foreground mb-2">
                  Check-out
                </label>
                <DatePicker
                  selected={checkoutDate}
                  onChange={(date) => {
                    setCheckoutDate(date);
                    onDatesChange?.({ checkin: checkinDate, checkout: date });
                  }}
                  minDate={checkinDate || today}
                  locale={enGB}
                  dateFormat="dd.MM.yyyy"
                  className="w-full h-10 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none"
                  placeholderText="dd.MM.yyyy"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Guests
              </label>
              <select
                value={guests}
                onChange={(e) => setGuests(Number(e.target.value))}
                className="w-full h-10 px-3 py-2 border border-input bg-background rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {[...Array(apartment?.capacity || 4)]?.map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1} {i + 1 === 1 ? "guest" : "guests"}
                  </option>
                ))}
              </select>
            </div>
            <SeasonalPreview
              months={monthsToShow}
              getRanges={getPriceRangesForMonth}
              checkinDate={checkinDate}
              checkoutDate={checkoutDate}
            />

            {nights < minStay && checkinDate && checkoutDate && (
              <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-2">
                Minimum {minStay} {minStay === 1 ? "night" : "nights"}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BookingPanel;
